'use client';

import { c as _c } from "react/compiler-runtime";
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownSection, DropdownItem } from "@heroui/react";
import { useAtom } from "jotai";
import { sortNftAtom, type SortType } from "@/config/atoms";
import { InlineIcon } from "@iconify/react/dist/iconify.js";
import { cn } from "@/util/cn";
import { hasMargin } from "@/util/style/conditions";

// Sort DropDown Component used to select Sort By Ascending, Descending and Rarity
// Uses NextUI Dropdown Component and has an onSortChanged event handler prop to handle the selection
// SortDropDownProps interface is used to define the props for the SortDropDown component
// The sort target is a key used to identify the part of the application that the sort is being applied to
// The sort value is the current value of the sort (Ascending, Descending, Rarity)
// The onSortChanged event handler is called when the user selects a new sort value
// a Jotai atom with storage is used to save the current sort value for the targeted section key
// when the SortDropDown component is rendered it will display the current sort value for the target key which is stored in the atom
// if there is no sort value stored in the atom for the target key it will default to Ascending

interface SortDropDownProps {
  sortTarget: string;
  sortValue?: string;
  onSortChanged?: (sortTarget: string, sortValue: string) => void;
  className?: string;
}

// SortDropDown Component
export default function SortDropDown(t0) {
  const $ = _c(33);
  const {
    sortTarget,
    className
  } = t0;
  const [sortAtom, setSortAtom] = useAtom(sortNftAtom);
  let t1;
  if ($[0] !== setSortAtom || $[1] !== sortAtom) {
    t1 = function sortChangeHandler(target, sortValue_0) {
      setSortAtom(prevConfig => ({
        ...prevConfig,
        [target]: sortValue_0
      }));
      console.log(sortAtom);
    };
    $[0] = setSortAtom;
    $[1] = sortAtom;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const sortChangeHandler = t1;
  const t2 = `relative justify-end hover:text-sky-400 active:text-sky-400 ${className && hasMargin(className) ? null : "m-2"} w-5 h-5`;
  let t3;
  if ($[3] !== className || $[4] !== t2) {
    t3 = cn(className, t2);
    $[3] = className;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <DropdownTrigger><InlineIcon icon="flowbite:sort-horizontal-outline" className={t3} /></DropdownTrigger>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = `${sortTarget}_${sortAtom[sortTarget]}`;
  let t6;
  if ($[8] !== t5) {
    t6 = [t5];
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = `${sortTarget}_Ascending`;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <InlineIcon icon="fluent:data-bar-vertical-ascending-16-filled" className="relative justify-end" />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== sortChangeHandler || $[12] !== sortTarget) {
    t9 = () => sortChangeHandler(sortTarget, "Ascending");
    $[11] = sortChangeHandler;
    $[12] = sortTarget;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t7 || $[15] !== t9) {
    t10 = <DropdownItem key={t7} startContent={t8} onPress={t9}>Ascending</DropdownItem>;
    $[14] = t7;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = `${sortTarget}_Descending`;
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <InlineIcon icon="fluent:data-bar-vertical-ascending-16-filled" className="relative justify-end scale-x-[-1]" />;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== sortChangeHandler || $[19] !== sortTarget) {
    t13 = () => sortChangeHandler(sortTarget, "Descending");
    $[18] = sortChangeHandler;
    $[19] = sortTarget;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t11 || $[22] !== t13) {
    t14 = <DropdownItem key={t11} startContent={t12} onPress={t13}>Descending</DropdownItem>;
    $[21] = t11;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t10 || $[25] !== t14) {
    t15 = <DropdownSection>{t10}{t14}</DropdownSection>;
    $[24] = t10;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t15 || $[28] !== t6) {
    t16 = <DropdownMenu aria-label="Sort By" variant="flat" disallowEmptySelection={true} selectionMode="single" selectedKeys={t6}>{t15}</DropdownMenu>;
    $[27] = t15;
    $[28] = t6;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t16 || $[31] !== t4) {
    t17 = <Dropdown>{t4}{t16}</Dropdown>;
    $[30] = t16;
    $[31] = t4;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  return t17;
}